/**
 * SirusHud (R232q78) — единый сборщик всех Sirus 1:1 панелей.
 *
 * Заменяет старый MVP-overlay в World.tsx и временный
 * PreSirusGameplayBlocked. Каждая панель сама позиционирует себя
 * через `position: fixed` и слушает свои keybind'ы — здесь только
 * монтаж в правильном порядке (z-index ladder), плюс модалка-обёртка
 * для toggleable панелей, которые не показываются по умолчанию.
 *
 * Z-index ladder:
 *   25 — Chat / QuestTracker
 *   30 — PlayerFrame, TargetFrame, Minimap, ActionBar, MicroMenu,
 *        CastBar, BuffBar
 *   40 — Bags, CharacterPane, QuestLog, SpellBook, TalentTree
 *   50 — SettingsPanel
 *   60 — UnitTooltip (mounted within hover sources, but reserve)
 */

import { useUI } from '../../state/store.js';
import { AchievementsPanel } from './AchievementsPanel.js';
import { ActionBar } from './ActionBar.js';
import { ActionBarExtras } from './ActionBarExtras.js';
import { Bags } from './Bags.js';
import { BuffBar } from './BuffBar.js';
import { CastBar } from './CastBar.js';
import { CharacterPane } from './CharacterPane.js';
import { Chat } from './Chat.js';
import { GroupFrame } from './GroupFrame.js';
import { HelpPanel } from './HelpPanel.js';
import { LfgPanel } from './LfgPanel.js';
import { MicroMenu, useUiPanels } from './MicroMenu.js';
import { Minimap } from './Minimap.js';
import { PlayerFrame } from './PlayerFrame.js';
import { PvpPanel } from './PvpPanel.js';
import { QuestLog } from './QuestLog.js';
import { QuestTracker } from './QuestTracker.js';
import { RaidFrame } from './RaidFrame.js';
import { SettingsPanel } from './SettingsPanel.js';
import { ShopPanel } from './ShopPanel.js';
import { BagBar } from './BagBar.js';
import { SocialPanel } from './SocialPanel.js';
import { SpellBook } from './SpellBook.js';
import { TalentTree } from './TalentTree.js';
import { TargetFrame } from './TargetFrame.js';
import { WorldMap } from './WorldMap.js';

export function SirusHud() {
  const stage = useUI((s) => s.stage);
  const panels = useUiPanels();

  // Hide HUD if не в world (на всякий случай — World.tsx уже гейтит).
  if (stage !== 'world') return null;

  return (
    <>
      {/* z-index 30 — always on */}
      <PlayerFrame />
      <TargetFrame />
      <GroupFrame />
      <RaidFrame />
      <Minimap />
      <ActionBar />
      <ActionBarExtras />
      <MicroMenu />
      <BagBar />
      <CastBar />
      <BuffBar />

      {/* z-index 25 — chat + quest tracker (background HUD) */}
      <Chat />
      <QuestTracker />

      {/* z-index 40 — toggleable panels (open through MicroMenu / keybinds) */}
      {panels.bagsAll && <Bags />}
      {panels.characterPane && <CharacterPane />}
      {panels.questLog && <QuestLog />}
      {panels.spellBook && <SpellBook />}
      <TalentTree open={panels.talentTree} onClose={() => panels.set('talentTree', false)} />
      {panels.achievements && <AchievementsPanel />}
      {panels.social && <SocialPanel />}
      {panels.lfg && <LfgPanel />}
      {panels.pvp && <PvpPanel />}
      {panels.help && <HelpPanel />}
      {panels.shop && <ShopPanel />}

      {/* z-index 45 — WorldMap (M key); компонент сам решает open-state */}
      <WorldMap />

      {/* z-index 50 — settings on top (mainMenu = ESC settings) */}
      {panels.mainMenu && <SettingsPanel />}
    </>
  );
}
